import React, { useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { Clear, Brightness1 } from '@mui/icons-material';
import {
  Grid,
  IconButton,
  TextField,
  Select,
  MenuItem,
  FormHelperText,
} from '@mui/material';
import { useDebouncedCallback } from 'use-debounce';

import { InventoryAdjustmentLineAction } from 'commons/enums';
import * as colors from 'commons/styles/colors';
import {
  InventoryAdjustmentProcessState,
  IGeneralTableIdentifierException,
  InventoryAdjustmentItems,
} from 'commons/types';
import {
  getInventoryAdjustmentProductInfo,
  postInventoryAdjustmentItem,
  postInventoryAdjustmentRemoveItem,
} from 'redux-stores/actions';
import { RootReducerInterface } from 'redux-stores/reducers';
import { AppDispatch } from 'redux-stores/store';

import { InventoryAdjustmentStyle as S } from '../styles/InventoryAdjustment.styles';

import { InventoryDetailModal } from './InventoryDetailModal.component';

interface SkuErrors {
  [key: number]: string;
}

interface IInventoryAdjustmentItemFormProps {
  processState: InventoryAdjustmentProcessState;
  setInventoryAdjustmentProcessState: (
    state: InventoryAdjustmentProcessState,
  ) => void;
  handleOpenDialog: (index: number) => void;
  setCompleteButtonDisabled: (value: boolean) => void;
}

export const InventoryAdjustmentItemForm: React.FC<
  IInventoryAdjustmentItemFormProps
> = ({
  processState,
  setInventoryAdjustmentProcessState,
  handleOpenDialog,
  setCompleteButtonDisabled,
}): React.JSX.Element[] => {
  const dispatch = useDispatch<AppDispatch>();
  const { activeLocations, itemListTableError } = useSelector(
    (state: RootReducerInterface) => state.inventoryAdjustment,
  );

  const [skuErrors, setSkuErrors] = useState<SkuErrors>({});

  const [inventoryDetailModalItem, setInventoryDetailModalItem] =
    useState<InventoryAdjustmentItems>({
      location: 0,
      sku_code: '',
      product_info: {
        sku_code: '',
        name: '',
        uom: '',
      },
      quantity_adjustment: 0,
      details: [
        {
          bin: '',
          inventory_number: '',
          expiry_date: new Date(),
          quantity: 0,
        },
      ],
    });
  const [inventoryDetailModalItemIndex, setInventoryDetailModalItemIndex] =
    useState<number>(0);

  const [openInventoryDetailModal, setOpenInventoryDetailModal] =
    useState<boolean>(false);

  const [adjustmentProgress, setAdjustmentProgress] = useState<
    Map<number, number>
  >(new Map());
  const [colorProgress, setColorProgress] = useState<Map<number, string>>(
    new Map(),
  );

  useMemo(() => {
    const clonedAdjustmentProgress = structuredClone(adjustmentProgress);
    const clonedColorProgress = structuredClone(colorProgress);

    // handle case if item length is 0,
    // set first index to gray for ui
    if (processState.items.length === 0) {
      clonedColorProgress.set(0, colors.PRIMARY_GRAY);
      clonedAdjustmentProgress.set(0, 0);
    }

    processState.items.forEach((item, index) => {
      let quantity = 0;

      const totalQty = item.quantity_adjustment;
      item.details.forEach((detail) => {
        quantity += detail.quantity;
      });
      clonedAdjustmentProgress.set(index, quantity);

      const color =
        quantity === 0
          ? colors.PRIMARY_GRAY
          : quantity === totalQty
          ? colors.PRIMARY_GREEN
          : Math.abs(quantity) > Math.abs(totalQty)
          ? colors.PRIMARY_RED
          : colors.PRIMARY_YELLOW;

      clonedColorProgress.set(index, color);
    });

    setAdjustmentProgress(clonedAdjustmentProgress);
    setColorProgress(clonedColorProgress);
  }, [processState.items]);

  useEffect(() => {
    setCompleteButtonDisabled(
      processState.adjustmentAccount === '' ||
        processState.postingPeriod === '' ||
        processState.memo === '' ||
        processState.items.length === 0 ||
        processState.items.some(
          (item, index) =>
            item.quantity_adjustment === 0 ||
            item.quantity_adjustment !== adjustmentProgress.get(index),
        ),
    );
  }, [adjustmentProgress]);

  const onInventoryDetailModalOpen = (
    item: InventoryAdjustmentItems,
    index: number,
  ) => {
    setInventoryDetailModalItem(item);
    setInventoryDetailModalItemIndex(index);
    setOpenInventoryDetailModal(true);
  };

  const onInventoryDetailModalClose = () => setOpenInventoryDetailModal(false);

  const debouncedProductInfo = useDebouncedCallback(
    async (params: {
      skuCode: string;
      state: InventoryAdjustmentProcessState;
      index: number;
    }) => {
      const { state, index, ...restParams } = params;

      if (!restParams.skuCode) {
        setInventoryAdjustmentProcessState({
          ...state,
          items: state.items.map((item, i) =>
            i === index
              ? {
                  ...item,
                  sku_code: '',
                  product_info: {
                    sku_code: '',
                    name: '',
                    uom: '',
                  },
                }
              : item,
          ),
        });
        return;
      }

      const result = await getInventoryAdjustmentProductInfo(
        { sku_code: restParams.skuCode },
        dispatch,
      );

      if (result) {
        setInventoryAdjustmentProcessState({
          ...state,
          items: state.items.map((item, i) =>
            i === index
              ? {
                  ...item,
                  sku_code: result.sku_code,
                  product_info: result,
                }
              : item,
          ),
        });
        setSkuErrors((prev) => {
          const updated = { ...prev };
          delete updated[index];
          return updated;
        });
      } else {
        setInventoryAdjustmentProcessState({
          ...state,
          items: state.items.map((item, i) =>
            i === index
              ? {
                  ...item,
                  sku_code: restParams.skuCode,
                  product_info: {
                    sku_code: '',
                    name: '',
                    uom: '',
                  },
                }
              : item,
          ),
        });
        setSkuErrors((prev) => ({
          ...prev,
          [index]: 'SKU not found',
        }));
      }
    },
    750,
    { leading: false, trailing: true },
  );

  const debouncedFocusNextField = useDebouncedCallback((selectorId: string) => {
    const nextInput = document.querySelector<HTMLInputElement>(selectorId);
    if (nextInput) nextInput.focus();
  }, 500);

  const debouncedInventoryAdjustmentItem = useDebouncedCallback(
    (params: {
      item: InventoryAdjustmentItems;
      index: number;
      action: InventoryAdjustmentLineAction;
    }) => {
      const { action, item, index } = params;
      if (action === InventoryAdjustmentLineAction.ADD) {
        postInventoryAdjustmentItem(
          {
            document_number: processState.documentNumber,
            item,
            index,
          },
          dispatch,
        );
      } else {
        postInventoryAdjustmentRemoveItem({
          document_number: processState.documentNumber,
          item_index: index,
        });
      }
    },
    500,
    {
      maxWait: 2000,
    },
  );

  const inventoryAdjustmentItems =
    processState.items.length > 0
      ? processState.items
      : [
          {
            location: 0,
            sku_code: '',
            product_info: { sku_code: '', name: '', uom: '' },
            quantity_adjustment: 0,
            details: [],
          },
        ];

  // render process
  return inventoryAdjustmentItems.map((item, idx) => {
    const rowTableError: IGeneralTableIdentifierException | null =
      itemListTableError.length > 0
        ? itemListTableError.find((data) => data.index === idx) || null
        : null;
    const replaceItem = (
      index: number,
      adjustmentItemList: InventoryAdjustmentItems[],
      newAdjustmentItem: InventoryAdjustmentItems,
    ): InventoryAdjustmentItems[] => {
      if (index < 0 && index >= adjustmentItemList.length) {
        return inventoryAdjustmentItems;
      }
      const updatedAdjusted = structuredClone(adjustmentItemList);
      const replaced = updatedAdjusted.splice(index, 1, newAdjustmentItem);
      if (replaced) {
        replaced.forEach((_item) => {
          debouncedInventoryAdjustmentItem({
            item: _item,
            index,
            action: InventoryAdjustmentLineAction.REMOVE,
          });
        });
      }

      return updatedAdjusted;
    };

    const onFieldChange = (
      type: keyof Pick<
        InventoryAdjustmentItems,
        'sku_code' | 'quantity_adjustment' | 'location'
      >,
      value: string,
      nextField?: string,
    ): void => {
      let clonedItems = structuredClone(inventoryAdjustmentItems);
      const prevValue = clonedItems[idx][type];
      const cleanNewValue = value.trim();
      if (type === 'sku_code' && clonedItems[idx].quantity_adjustment !== 0) {
        const newItem: InventoryAdjustmentItems = {
          location: 0,
          sku_code: '',
          product_info: {
            sku_code: '',
            name: '',
            uom: '',
          },
          quantity_adjustment: 0,
          details: [],
        };
        newItem[type] = cleanNewValue;
        clonedItems = replaceItem(idx, clonedItems, newItem);
      } else if (
        /*
          data type number
          need to be in this chained OR format, so the else block could executed properly 
          */
        type === 'quantity_adjustment'
      ) {
        if (!Number.isNaN(cleanNewValue)) {
          clonedItems[idx].quantity_adjustment = parseInt(cleanNewValue, 10);
        }
      } else if (type === 'location') {
        clonedItems[idx].location = parseInt(cleanNewValue, 10);
      } else {
        clonedItems[idx][type] = cleanNewValue;
      }
      const updatedState = {
        ...processState,
        items: clonedItems,
      };

      if (type === 'sku_code') {
        debouncedProductInfo({
          skuCode: updatedState.items[idx].sku_code,
          index: idx,
          state: updatedState,
        });
      }

      setInventoryAdjustmentProcessState(updatedState);

      const {
        sku_code: skuCode,
        quantity_adjustment: quantityAdjustment,
        location,
      } = updatedState.items[idx];

      if (skuCode && quantityAdjustment && location) {
        debouncedInventoryAdjustmentItem({
          item: clonedItems[idx],
          index: idx,
          action: InventoryAdjustmentLineAction.ADD,
        });
      }

      if (
        nextField &&
        (value.endsWith('\t') ||
          value.endsWith('\n') ||
          (!prevValue && value.length > 1))
      ) {
        debouncedFocusNextField(`input[id="field-adjust-${nextField}-${idx}"]`);
      }
    };

    return (
      <>
        <Grid container style={{ marginBottom: '4px' }}>
          {/* Row content */}
          <S.FieldRowWrapper
            item
            xs={0.5}
            container
            alignItems="center"
            style={{ padding: 0 }}
          >
            <IconButton
              onClick={() => handleOpenDialog(idx)}
              style={{ color: colors.PRIMARY_GRAY }}
            >
              <Clear />
            </IconButton>
          </S.FieldRowWrapper>
          <S.FieldRowWrapper item xs={2}>
            SKU Code
            <TextField
              variant="outlined"
              placeholder="-"
              value={item.sku_code}
              onChange={(event) =>
                onFieldChange('sku_code', event.target.value, 'uom')
              }
              id={`field-adjust-sku-${idx}`}
              fullWidth
              error={!!skuErrors[idx] || !!rowTableError?.identifier?.sku_code}
              helperText={skuErrors[idx]}
              FormHelperTextProps={{
                sx: {
                  position: 'absolute',
                  bottom: '-20px',
                  padding: 0,
                  margin: 0,
                },
              }}
              disabled={(adjustmentProgress.get(idx) || 0) !== 0}
            />
          </S.FieldRowWrapper>
          <S.FieldRowWrapper item xs={3}>
            UOM
            <TextField
              variant="outlined"
              placeholder="-"
              value={item.product_info.uom}
              error={!!rowTableError?.identifier?.uom}
              disabled
              fullWidth
            />
          </S.FieldRowWrapper>
          <S.FieldRowWrapper item xs={1}>
            QTY
            <TextField
              variant="outlined"
              placeholder="0"
              type="number"
              value={item.quantity_adjustment || ''}
              error={!!rowTableError?.identifier?.quantity_adjustment}
              onChange={(event) =>
                onFieldChange(
                  'quantity_adjustment',
                  event.target.value,
                  'location',
                )
              }
              id={`field-adjust-quantity-adjustment-${idx}`}
              fullWidth
              disabled={
                (adjustmentProgress.get(idx) || 0) !== 0 &&
                !rowTableError?.identifier?.quantity_adjustment
              }
            />
          </S.FieldRowWrapper>
          <S.FieldRowWrapper item xs={3.5}>
            Location
            <Select
              value={item.location === 0 ? '' : item.location.toString()}
              onChange={(event) =>
                onFieldChange('location', event.target.value)
              }
              id={`field-adjust-location-${idx}`}
              fullWidth
              disabled={
                (adjustmentProgress.get(idx) || 0) !== 0 &&
                !rowTableError?.identifier?.location
              }
              error={!!rowTableError?.identifier?.location}
            >
              {activeLocations.map((loc) => (
                <MenuItem value={loc.warehouse_id.toString()}>
                  {loc.name}
                </MenuItem>
              ))}
            </Select>
          </S.FieldRowWrapper>
          <S.FieldRowWrapper
            item
            container
            xs={2}
            direction="column"
            justifyContent="flex-end"
          >
            <S.InventoryDetailButton
              onClick={() => onInventoryDetailModalOpen(item, idx)}
              disabled={
                !!skuErrors[idx] ||
                item.product_info.name === '' ||
                !item.quantity_adjustment ||
                item.location === 0
              }
            >
              Inventory Details
              <Brightness1
                style={{
                  fontSize: 12,
                  color: colorProgress.get(idx),
                  marginLeft: '8px',
                }}
              />
            </S.InventoryDetailButton>
          </S.FieldRowWrapper>

          {/* Row Error message */}
          {itemListTableError.length > 0 && (
            <>
              <S.FieldErrorRowWrapper
                item
                xs={0.5}
                container
                alignItems="center"
                style={{ padding: 0 }}
              />
              <S.FieldErrorRowWrapper item xs={2}>
                {!!rowTableError?.identifier?.sku_code && (
                  <FormHelperText error>
                    {rowTableError?.identifier?.sku_code || ''}
                  </FormHelperText>
                )}
              </S.FieldErrorRowWrapper>
              <S.FieldErrorRowWrapper item xs={3}>
                {!!rowTableError?.identifier?.uom && (
                  <FormHelperText error>
                    {rowTableError?.identifier?.uom || ''}
                  </FormHelperText>
                )}
              </S.FieldErrorRowWrapper>
              <S.FieldErrorRowWrapper item xs={1}>
                {!!rowTableError?.identifier?.quantity_adjustment && (
                  <FormHelperText error>
                    {rowTableError?.identifier?.quantity_adjustment || ''}
                  </FormHelperText>
                )}
              </S.FieldErrorRowWrapper>
              <S.FieldErrorRowWrapper item xs={3.5}>
                {!!rowTableError?.identifier?.location && (
                  <FormHelperText error>
                    {rowTableError?.identifier?.location || ''}
                  </FormHelperText>
                )}
              </S.FieldErrorRowWrapper>
              <S.FieldErrorRowWrapper
                item
                container
                xs={2}
                direction="column"
                justifyContent="flex-end"
              >
                {!!rowTableError?.identifier?.details && (
                  <FormHelperText error>
                    {rowTableError?.identifier?.details || ''}
                  </FormHelperText>
                )}
              </S.FieldErrorRowWrapper>
            </>
          )}
        </Grid>
        {openInventoryDetailModal && (
          <InventoryDetailModal
            onClose={onInventoryDetailModalClose}
            openModal={openInventoryDetailModal}
            items={inventoryDetailModalItem}
            itemsIndex={inventoryDetailModalItemIndex}
            documentNumber={processState.documentNumber}
          />
        )}
      </>
    );
  });
};
